import { parseHelpDoc, type ParsedHelpDoc } from './frontmatter';

type RawLoader = () => Promise<string>;

const rawLoaders = import.meta.glob('/resources/help/*.md', {
    query: '?raw',
    import: 'default',
    eager: false,
}) as Record<string, RawLoader>;

const keyFromPath = (path: string): string => {
    const match = /\/resources\/help\/(.+)\.md$/.exec(path);
    return match?.[1] ?? path;
};

const docCache = new Map<string, ParsedHelpDoc>();
let indexCache: HelpDocMeta[] | null = null;

export interface HelpDocMeta {
    key: string;
    title: string;
    keywords: string[];
    related: string[];
    updatedAt: string | null;
}

function resolvePath(key: string, locale?: string): string | null {
    if (locale) {
        const localePath = `/resources/help/${key}.${locale}.md`;
        if (rawLoaders[localePath]) {
            return localePath;
        }
    }

    const path = `/resources/help/${key}.md`;
    return rawLoaders[path] ? path : null;
}

export async function loadHelpDoc(key: string, locale?: string): Promise<ParsedHelpDoc | null> {
    const cacheKey = locale ? `${key}::${locale}` : key;
    const cached = docCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const path = resolvePath(key, locale);
    if (!path) {
        return null;
    }

    const load = rawLoaders[path];
    if (!load) {
        return null;
    }

    const raw = await load();
    const parsed = parseHelpDoc(raw);
    docCache.set(cacheKey, parsed);
    return parsed;
}

export async function listHelpDocs(): Promise<HelpDocMeta[]> {
    if (indexCache) {
        return indexCache;
    }

    const entries = await Promise.all(
        Object.entries(rawLoaders).map(async ([path, load]) => {
            const key = keyFromPath(path);

            if (/\.[a-z]{2}(-[A-Z]{2})?$/.test(key)) {
                return null;
            }

            const raw = await load();
            const { frontmatter } = parseHelpDoc(raw);

            return {
                key,
                title: frontmatter.title || key,
                keywords: frontmatter.keywords,
                related: frontmatter.related,
                updatedAt: frontmatter.updatedAt,
            } satisfies HelpDocMeta;
        }),
    );

    indexCache = entries.filter((e): e is HelpDocMeta => e !== null).toSorted((a, b) => a.title.localeCompare(b.title));
    return indexCache;
}

export function searchHelpDocs(query: string, docs: HelpDocMeta[]): HelpDocMeta[] {
    const q = query.trim().toLowerCase();
    if (!q) {
        return docs;
    }

    const tokens = q.split(/\s+/).filter(Boolean);

    return docs
        .map((doc) => {
            const haystack = `${doc.title}\n${doc.keywords.join(' ')}\n${doc.key}`.toLowerCase();
            const titleLc = doc.title.toLowerCase();

            let score = 0;
            for (const token of tokens) {
                if (titleLc.includes(token)) {
                    score += 3;
                } else if (haystack.includes(token)) {
                    score += 1;
                } else {
                    score = 0;
                    break;
                }
            }

            return { doc, score };
        })
        .filter((entry) => entry.score > 0)
        .toSorted((a, b) => b.score - a.score)
        .map((entry) => entry.doc);
}

export function resetHelpCaches(): void {
    docCache.clear();
    indexCache = null;
}
